import DoneIcon from '@mui/icons-material/Done'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import { Box, Chip, ChipProps, Stack, Tooltip, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import { FDRStatus, FDRStatusEntry, FieldModel } from '@cibo/core'
import { ProgramIcon, ProgramProgressChip, useProgram } from '../..'
import { WorkflowStatusChip } from './WorkflowStatusChip'

interface StatusChipProps extends ChipProps {
  // @todo support other tokens!
  dollarPotential?: number
  fdrStatusEntry: FDRStatusEntry
  workflowId?: string
  programId: string
  fieldModel: FieldModel
}

export const StatusChip = ({
  dollarPotential,
  fdrStatusEntry,
  fieldModel,
  programId,
  workflowId,
  ...chipProps
}: StatusChipProps) => {
  const { t } = useTranslation('@cibo/landmanager/FieldsTable')
  const program = useProgram({ programId: fdrStatusEntry.programId })
  const windowClosed = program?.data?.enrollmentWindow.enrollmentStatus === 'closed'
  const enrolledAt = fieldModel.program(programId)?.enrolledAt
  const isEngaged =
    fdrStatusEntry.status.includes(FDRStatus.isEngaged) &&
    !fdrStatusEntry.status.includes(FDRStatus.enrolled)
  const isEnrolling = fdrStatusEntry.status.includes(FDRStatus.enrolling)
  const isUnderReview = fdrStatusEntry.status.includes(FDRStatus.underReview)
  const isEnrolled = fdrStatusEntry.status.includes(FDRStatus.enrolled)
  const isContracted = fdrStatusEntry.status.includes(FDRStatus.contracted)
  const isCompleted = fdrStatusEntry.status.includes(FDRStatus.completed)

  /**
   * @todo
   * It is possible for a field to both be associated with a workflow
   * and be participating.  If the field is particiapting, we want to show
   * the "enrolled" state
   *
   * The todo is to figure out on the backend an authoritative state for this
   * field as it relates to the program
   */
  if (isEngaged)
    return (
      <Tooltip
        placement="top-start"
        title={
          <Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {!!program?.data && <ProgramIcon size={24} program={program.data} />}
              <Typography>{program.data?.content.fields.title}</Typography>
            </Stack>
          </Stack>
        }
      >
        <Stack alignSelf="start">
          <Box>
            <ProgramProgressChip
              engageType={program.data?.engageType}
              progress={windowClosed ? 'window_closed' : 'engaged'}
              {...chipProps}
            />
          </Box>
          {!!dollarPotential && !windowClosed && (
            <Typography variant="caption" sx={{ paddingLeft: 3.25 }}>
              {t('potential', { context: 'usd', count: dollarPotential })}
            </Typography>
          )}
        </Stack>
      </Tooltip>
    )

  if (isEnrolling) {
    return (
      <WorkflowStatusChip
        {...{ dollarPotential, fdrStatusEntry, fieldModel, programId, workflowId, ...chipProps }}
      />
    )
  }

  if (isEnrolled)
    return (
      <Stack>
        <Tooltip
          placement="top-start"
          title={
            <Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                {!!program?.data && <ProgramIcon size={24} program={program.data} />}
                <Typography>{program.data?.content.fields.title}</Typography>
              </Stack>
              {!!enrolledAt && (
                <Typography variant="caption" color="primary.contrastText">
                  {t('enrolledAt', { enrolledAt: dayjs(enrolledAt).toDate() })}
                </Typography>
              )}
            </Stack>
          }
        >
          <Box alignSelf="start">
            {isCompleted ? (
              <Chip
                {...chipProps}
                color="success"
                icon={<DoneAllIcon />}
                label={t('completed', { ns: '@cibo/programs/StatusChip' })}
                clickable={chipProps.onClick !== undefined}
              />
            ) : isContracted ? (
              <Chip
                {...chipProps}
                color="success"
                icon={<DoneIcon />}
                label={t('contracted', { ns: '@cibo/programs/StatusChip' })}
                clickable={chipProps.onClick !== undefined}
              />
            ) : (
              <ProgramProgressChip
                progress={isUnderReview ? 'under_review' : 'enrolled'}
                {...chipProps}
              />
            )}
          </Box>
        </Tooltip>
        {!!dollarPotential && (
          <Typography variant="caption" sx={{ paddingLeft: 3 }}>
            {t('potential', { context: 'usd', count: dollarPotential })}
          </Typography>
        )}
      </Stack>
    )

  return null
}
